import { baseApi } from '@/store/api/baseApi';
import { API_URL } from '@/config/app';
import type { RootState } from '@/store';
import type { ChangePasswordPayload, UpdateProfilePayload, User } from '@/types/auth';
import { createAccessToken } from '@/utils/agent';
import { getRtkErrorMessage } from '@/utils/apiResponse';
import { isApiResponse } from '@/types/api';
import { normalizeUser } from '@/utils/user';

export const userApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getUserDetail: builder.query<User, void>({
      query: () => '/users/detail',
      transformResponse: (response: User) => normalizeUser(response),
      providesTags: ['Auth'],
    }),
    updateProfile: builder.mutation<User, UpdateProfilePayload>({
      query: (body) => ({
        url: '/users/update-profile',
        method: 'POST',
        body,
      }),
      transformResponse: (response: User) => normalizeUser(response),
      invalidatesTags: ['Auth'],
    }),
    updateProfileImage: builder.mutation<User, FormData>({
      async queryFn(formData, api) {
        const { accessToken } = (api.getState() as RootState).auth;

        try {
          const response = await fetch(`${API_URL}/users/update-profile-image`, {
            method: 'POST',
            headers: {
              ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
              'x-access-token': createAccessToken(),
            },
            body: formData,
          });

          const json: unknown = await response.json();

          if (!response.ok) {
            return {
              error: {
                status: response.status,
                data: getRtkErrorMessage({ data: json }) ?? 'Image upload failed.',
              },
            };
          }

          const rawUser = isApiResponse<User>(json) ? json.data : (json as User);
          const message = isApiResponse<User>(json) ? json.message : undefined;

          return {
            data: normalizeUser(rawUser),
            ...(message ? { meta: { message } } : {}),
          };
        } catch {
          return {
            error: {
              status: 'FETCH_ERROR',
              error: 'Image upload failed.',
            },
          };
        }
      },
      invalidatesTags: ['Auth'],
    }),
    updatePassword: builder.mutation<void, ChangePasswordPayload>({
      query: (body) => ({
        url: '/users/update-password',
        method: 'POST',
        body,
      }),
    }),
  }),
});

export const {
  useGetUserDetailQuery,
  useUpdateProfileMutation,
  useUpdateProfileImageMutation,
  useUpdatePasswordMutation,
} = userApi;
